import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../utils/api';
import { storedRefCode } from '../services/creatorApi';

interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  // Newsletter opt-in surface — reads from /api/auth/profile/. consent_at = null
  // means the user has never made a choice (gates the post-signup welcome modal).
  newsletter_consent?: boolean;
  newsletter_consent_at?: string | null;
  newsletter_consent_locked?: boolean;
}

interface UserProfile {
  id: string;
  current_plan: {
    name: string;
    max_keywords: number;
    max_mentions_per_month: number;
    price_monthly: string;
  };
  subscription_status: string;
  current_month_mentions: number;
  usage_percentage: number;
  subscription_end_date: string | null;
}

// Discriminated result for flows that may pause on email verification.
// login/register resolve to either an authenticated success or a signal that
// the caller must render the OTP step for `email`.
export type AuthResult =
  | { status: 'success' }
  | { status: 'verification_required'; email: string };

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  token: string | null;
  login: (username: string, password: string) => Promise<AuthResult>;
  loginWithGoogle: (credential: string) => Promise<void>;
  logout: () => void;
  register: (data: any) => Promise<AuthResult>;
  verifyEmail: (email?: string, code?: string, token?: string) => Promise<void>;
  resendVerification: (email: string) => Promise<void>;
  refreshProfile: () => Promise<void>;
  isAuthenticated: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper function to extract user-friendly error message from API response
const extractErrorMessage = (errorData: any): string => {
  if (typeof errorData === 'string') {
    return errorData;
  }

  if (errorData?.error) {
    return errorData.error;
  }

  if (errorData?.errors && typeof errorData.errors === 'object') {
    // Handle field errors like {"email": ["error message"]}
    for (const field in errorData.errors) {
      const fieldErrors = errorData.errors[field];
      if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
        return fieldErrors[0];
      }
    }
  }

  return 'Operation failed';
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('authToken'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchProfile = async (authToken?: string) => {
    // Use passed token or get from localStorage (more reliable than state)
    const currentToken = authToken || localStorage.getItem('authToken');
    if (!currentToken) {
      setLoading(false);
      return;
    }

    try {
      // Try the Google OAuth profile endpoint first
      const response = await fetch('/api/auth/profile/', {
        headers: {
          'Authorization': `Token ${currentToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUser({
          id: data.id,
          username: data.name?.split(' ')[0]?.toLowerCase() || data.email?.split('@')[0] || 'user',
          email: data.email,
          first_name: data.name?.split(' ')[0] || '',
          last_name: data.name?.split(' ').slice(1).join(' ') || '',
          newsletter_consent: data.newsletter_consent ?? false,
          newsletter_consent_at: data.newsletter_consent_at ?? null,
          newsletter_consent_locked: data.newsletter_consent_locked ?? false,
        });
        setProfile(null); // Google OAuth users don't have the old SaaS profile
        setLoading(false);
        return;
      }

      // Fallback to old SaaS profile endpoint
      const saasResponse = await authAPI.getProfile();
      setUser(saasResponse.data.user);
      setProfile(saasResponse.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching profile:', error);
      logout();
    }
  };

  const refreshProfile = async () => {
    await fetchProfile();
  };

  // Single source of truth for "a token came back → we're logged in". Reused by
  // login success AND verify-email success so a verified user lands exactly like
  // a normal login (token in localStorage + state, profile hydrated).
  const applySession = (authToken: string, userData?: any, profileData?: any) => {
    localStorage.setItem('authToken', authToken);
    setToken(authToken);
    if (userData !== undefined) setUser(userData);
    if (profileData !== undefined) setProfile(profileData);
  };

  const login = async (username: string, password: string): Promise<AuthResult> => {
    try {
      const response = await authAPI.login({ username, password });
      const { token: authToken, user: userData, profile: profileData } = response.data;
      applySession(authToken, userData, profileData);
      return { status: 'success' };
    } catch (error: any) {
      const data = error.response?.data;
      // Unverified account: backend returns 403 {verification_required, email}.
      // Surface it distinctly so the caller can show the OTP step.
      if (error.response?.status === 403 && data?.verification_required) {
        return { status: 'verification_required', email: data.email };
      }
      throw new Error(data?.error || 'Login failed');
    }
  };

  const register = async (data: any): Promise<AuthResult> => {
    try {
      const refCode = storedRefCode();
      const payload = refCode ? { ...data, ref_code: refCode } : data;
      const response = await authAPI.register(payload);
      const resData = response.data;

      // New flow: register returns 201 {verification_required, email} and NO
      // token. Do NOT store a token or set authenticated state — signal the
      // caller to render the OTP step. (Fixes the old bug that stored the string
      // "undefined" as authToken and navigated to a broken dashboard.)
      if (resData?.verification_required) {
        return { status: 'verification_required', email: resData.email };
      }

      // Legacy/unexpected token path (kept for safety).
      if (resData?.token) {
        applySession(resData.token, resData.user, resData.profile);
        return { status: 'success' };
      }
      return { status: 'verification_required', email: payload.email };
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error.response?.data);
      throw new Error(errorMessage);
    }
  };

  // Verify by (email, code) OR by (token) alone. On success the server returns
  // the same {token, user} payload as login — reuse applySession so the user is
  // logged in immediately. Errors re-throw the backend code ('invalid' |
  // 'expired' | 'too_many_attempts') for the UI to map.
  const verifyEmail = async (email?: string, code?: string, token?: string) => {
    try {
      const response = await authAPI.verifyEmail({ email, code, token });
      const { token: authToken, user: userData, profile: profileData } = response.data;
      applySession(authToken, userData, profileData);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'invalid');
    }
  };

  // Always resolves (server returns 200 {sent:true} regardless). Errors are
  // swallowed to a resolved promise so the UI countdown can proceed uniformly.
  const resendVerification = async (email: string) => {
    try {
      await authAPI.resendVerification(email);
    } catch (error: any) {
      // Non-fatal: the user can retry. Never leak account existence.
    }
  };

  const loginWithGoogle = async (credential: string) => {
    try {
      const refCode = storedRefCode();
      const response = await fetch('/api/auth/google/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(refCode ? { credential, ref_code: refCode } : { credential }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Google login failed');
      }

      const data = await response.json();
      const { token: authToken, user: userData } = data;

      // Store token in localStorage first (synchronous)
      localStorage.setItem('authToken', authToken);

      // Update state
      setToken(authToken);
      setUser({
        id: userData.id,
        username: userData.name?.split(' ')[0]?.toLowerCase() || userData.email.split('@')[0],
        email: userData.email,
        first_name: userData.name?.split(' ')[0] || '',
        last_name: userData.name?.split(' ').slice(1).join(' ') || '',
      });
      setLoading(false);
    } catch (error: any) {
      throw new Error(error.message || 'Google login failed');
    }
  };

  const logout = () => {
    setUser(null);
    setProfile(null);
    setToken(null);
    localStorage.removeItem('authToken');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        token,
        login,
        loginWithGoogle,
        logout,
        register,
        verifyEmail,
        resendVerification,
        refreshProfile,
        isAuthenticated: !!token,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
